import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import KNDataTable from 'components/KNDataTable/KNDataTable'
import type { KNDataTableAction, KNDataTableColumn } from 'components/KNDataTable/types'
import KNConfirmationDialog from 'components/KNDialog/KNConfirmationDialog'
import { analyticsEvent } from 'global/helpers/analytics'
import useDialog from 'global/hooks/useDialog'
import { type ReactElement, useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CONTACTS_QUERY_KEY, deleteContact } from './CarrierProfile.service'
import type { Contact } from './CarrierProfile.types'
import CreateOrEditContactDialog from './CreateOrEditContactDialog'

interface ContactTableProps {
  readonly contacts: Contact[]
}

const ContactsTable = ({
  contacts,
}: ContactTableProps): ReactElement => {
  const { t } = useTranslation()
  const [activeContact, setActiveContact] = useState<Contact>()
  const queryClient = useQueryClient()
  const deleteContactMutation = useMutation({
    mutationFn: deleteContact,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [CONTACTS_QUERY_KEY] }),
  })
  const editContactDialog = useDialog<typeof CreateOrEditContactDialog>()

  const handleEditClick = useCallback((row: Contact) => {
    setActiveContact(row)
    editContactDialog.handleOpen()
  }, [])

  const handleDeleteContactDialogAction = useCallback(async () => {
    if (!activeContact) {
      return
    }
    await deleteContactMutation.mutateAsync(activeContact.id)
    analyticsEvent('polestar_carrier_profile_contact_removed')
  }, [activeContact])

  const deleteContactDialog = useDialog<typeof KNConfirmationDialog>({
    onAction: handleDeleteContactDialogAction,
  })

  const handleDeleteClick = useCallback((row: Contact) => {
    setActiveContact(row)
    deleteContactDialog.handleOpen()
  }, [])

  const actions = useMemo((): KNDataTableAction<Contact>[] => [
    {
      name: 'edit',
      label: t('screens.cs.carrier_profile.contacts.edit_contact'),
      icon: <EditIcon />,
      onClick: handleEditClick,
    },
    {
      name: 'delete',
      label: t('general.delete'),
      icon: <DeleteIcon />,
      onClick: handleDeleteClick,
    },
  ], [])

  const nameColumnValue = (row: Contact): ReactElement => (
    <Typography variant="p3" color="primary.main" noWrap>
      {row.name}
    </Typography>
  )

  const rolesColumnValue = (row: Contact): ReactElement => (
    <Stack spacing={0.5}>
      {row.roles?.map((role) => (
        <Typography key={role} variant="p3" color="primary.main" noWrap>
          {t(`screens.cs.carrier_profile.contacts.${role}`)}
        </Typography>
      ))}
    </Stack>
  )

  const locationColumnValue = (row: Contact): ReactElement => (
    <Typography component="p" variant="p3" color="primary.main" noWrap>
      {row.location}
    </Typography>
  )

  const emailColumnValue = (row: Contact): ReactElement => (
    <Typography component="p" variant="p3" color="primary.main" noWrap>
      {row.email}
    </Typography>
  )

  const phoneColumnValue = (row: Contact): ReactElement => (
    <Typography component="p" variant="p3" color="primary.main" noWrap>
      {row.phone}
    </Typography>
  )

  const columns = useMemo((): KNDataTableColumn<Contact>[] => [
    {
      name: 'name',
      label: t('screens.cs.carrier_profile.contacts.name'),
      getValue: nameColumnValue,
      sx: { width: { xs: '10rem', xxl: 'auto' } },
    },
    {
      name: 'roles',
      label: t('screens.cs.carrier_profile.contacts.roles'),
      getValue: rolesColumnValue,
      sx: { width: { xs: '10rem', xxl: '14rem' } },
    },
    {
      name: 'location',
      label: t('screens.cs.carrier_profile.contacts.location'),
      getValue: locationColumnValue,
      sx: { width: { xs: '10rem', xxl: '14rem' } },
    },
    {
      name: 'email',
      label: t('screens.cs.carrier_profile.contacts.email'),
      getValue: emailColumnValue,
      sx: { width: { xs: '10rem', xxl: '14rem' } },
    },
    {
      name: 'phone',
      label: t('screens.cs.carrier_profile.contacts.phone'),
      getValue: phoneColumnValue,
      sx: { width: { xs: '10rem', xxl: '14rem' } },
    },
  ], [])

  return (
    <>
      <KNDataTable
        columns={columns}
        actions={actions}
        data={contacts}
        sx={{
          marginX: -2,
        }}
      />
      {activeContact && (
        <>
          <CreateOrEditContactDialog
            payload={{
              contact: activeContact,
            }}
            open={editContactDialog.isOpen}
            onAction={editContactDialog.handleAction}
            onClose={editContactDialog.handleClose}
          />
          <KNConfirmationDialog
            message={t('screens.cs.carrier_profile.contacts.delete_confirmation')}
            continueLabel={t('general.delete')}
            open={deleteContactDialog.isOpen}
            onAction={deleteContactDialog.handleAction}
            onClose={deleteContactDialog.handleClose}
          />
        </>
      )}
    </>
  )
}

export default ContactsTable
